import { addAction } from 'lib/tracking/datadog'

interface ReloadOnceForStaleDeployParams {
  /**
   * `sessionStorage` key that gates the reload loop. Keep it distinct per recovery flow — a shared key
   * would let one flow's reload suppress another's within the guard window.
   */
  guardKey: string
  /** Datadog action recorded immediately before the reload fires. */
  action: string
  /**
   * Invoked on every branch that claims the error but skips the reload (SSR, unavailable storage, a
   * repeat within the guard window, or an offline tab). Lets the caller decide what to do instead —
   * log the error, or nothing.
   */
  onSkipReload?: () => void
}

const RELOAD_GUARD_WINDOW_MS = 10_000

/**
 * Reloads the page once to recover from a stale-deploy error (a superseded build the browser is still
 * running), guarded against a reload loop by a `sessionStorage` timestamp. Call only after the caller
 * has recognized and taken ownership of its error type — this helper does not inspect the error.
 *
 * Reloads when on the client, online, and outside the guard window; otherwise invokes `onSkipReload`
 * (SSR, unavailable storage, a repeat within the window, or an offline tab). `addAction` fires only on
 * the reload path, so it and `onSkipReload` are mutually exclusive.
 */
export const reloadOnceForStaleDeploy = ({ guardKey, action, onSkipReload }: ReloadOnceForStaleDeployParams): void => {
  if (typeof window === 'undefined') {
    onSkipReload?.()
    return
  }

  let lastAttemptAt: number | null = null

  try {
    const stored = window.sessionStorage.getItem(guardKey)

    lastAttemptAt = stored === null ? null : Number(stored)
  } catch {
    onSkipReload?.()
    return
  }

  const recentlyAttempted = lastAttemptAt !== null && Date.now() - lastAttemptAt < RELOAD_GUARD_WINDOW_MS
  const isOffline = window.navigator.onLine === false

  if (recentlyAttempted || isOffline) {
    onSkipReload?.()
    return
  }

  try {
    window.sessionStorage.setItem(guardKey, String(Date.now()))
  } catch {
    onSkipReload?.()
    return
  }

  addAction(action)
  window.location.reload()
}
